import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Pause, RotateCcw, ArrowRight, Sparkles, ImageOff, PlayCircle } from "lucide-react";

import { MarketingLayout, Section, Eyebrow, TrialButton, useSeo } from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { publicService } from "@/services/publicService";
import { TOUR_SCENES, TOUR_PERSONA, TOUR_AUTOPLAY_MS, TOUR_VIDEO } from "@/config/demoTour";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

/* Real screenshot with subtle framing + accessible callouts. Falls back to a
   branded placeholder if a screenshot asset is not yet available. */
function SceneStage({ scene, active }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [scene.id]);
  return (
    <div className="relative overflow-hidden rounded-xl border border-black/10 bg-brand-light-gray shadow-sm">
      <div className="relative aspect-[16/10] w-full">
        {failed ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-brand-navy/[0.03] p-6 text-center" data-testid="demo-tour-image-missing">
            <ImageOff className="h-7 w-7 text-brand-navy/40" />
            <p className="font-brand text-lg font-bold text-brand-navy">{scene.title}</p>
            <p className="max-w-sm text-xs text-charcoal/55">{scene.alt}</p>
          </div>
        ) : (
          <img
            src={scene.image}
            alt={scene.alt}
            data-testid="demo-tour-image"
            loading={active ? "eager" : "lazy"}
            onError={() => setFailed(true)}
            className="h-full w-full object-cover object-top"
          />
        )}
        {/* Positioned callouts (hidden on small screens; shown as chips below) */}
        {!failed && (
          <div className="pointer-events-none absolute inset-0 hidden sm:block" aria-hidden="true">
            {scene.callouts.map((c, i) => (
              <span
                key={c.label}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${c.x}%`, top: `${c.y}%` }}
              >
                <span className="flex items-center gap-1.5 rounded-full border border-brand-gold/60 bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-brand-navy shadow-md ring-1 ring-brand-gold/20 backdrop-blur">
                  <span className="grid h-4 w-4 place-items-center rounded-full bg-brand-gold text-[10px] font-bold text-brand-navy">{i + 1}</span>
                  {c.label}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
      {/* Mobile chip list of highlighted areas */}
      {!failed && scene.callouts.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 border-t border-black/5 bg-white px-3 py-2.5 sm:hidden" data-testid="demo-tour-callout-chips">
          {scene.callouts.map((c, i) => (
            <li key={c.label} className="flex items-center gap-1.5 rounded-full bg-brand-light-gray px-2.5 py-1 text-[11px] font-semibold text-brand-navy">
              <span className="grid h-4 w-4 place-items-center rounded-full bg-brand-gold text-[10px] font-bold text-brand-navy">{i + 1}</span>
              {c.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DemoTour() {
  useSeo({
    title: "See AgentHQ in Action",
    path: "/demo",
    description: "Take an interactive, guided walkthrough of AgentHQ — the business operating system for real estate professionals. See the Dashboard, CRM, Pipeline, Transactions, Marketing, Compliance, Goals, and RESA.",
  });

  const reduced = usePrefersReducedMotion();
  const scenes = TOUR_SCENES;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [founding, setFounding] = useState(null);
  const timer = useRef(null);

  const last = scenes.length - 1;
  const scene = scenes[index];
  const atEnd = index === last;

  useEffect(() => { publicService.foundingStatus().then(setFounding).catch(() => setFounding(null)); }, []);

  const goto = useCallback((i) => setIndex(Math.max(0, Math.min(last, i))), [last]);
  const manualStop = useCallback(() => setPlaying(false), []);
  const next = useCallback(() => { manualStop(); goto(index + 1); }, [index, goto, manualStop]);
  const prev = useCallback(() => { manualStop(); goto(index - 1); }, [index, goto, manualStop]);
  const restart = useCallback(() => { setPlaying(false); setIndex(0); }, []);

  // Calm autoplay; never runs under reduced-motion; stops at the end.
  useEffect(() => {
    if (!playing || reduced) return;
    if (atEnd) { setPlaying(false); return; }
    timer.current = setTimeout(() => setIndex((i) => Math.min(last, i + 1)), TOUR_AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [playing, reduced, index, atEnd, last]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
  };

  const foundingOpen = founding && founding.enrollment_open && founding.remaining > 0;
  const hasVideo = Boolean(TOUR_VIDEO && TOUR_VIDEO.url);

  return (
    <MarketingLayout>
      <div className="bg-brand-navy text-white">
        <Section className="py-12 sm:py-16">
          <Eyebrow>See AgentHQ in action</Eyebrow>
          <h1 className="mt-3 font-brand text-3xl font-bold leading-tight sm:text-4xl">A guided walkthrough of AgentHQ.</h1>
          <p className="mt-3 max-w-2xl text-white/70">
            Click through the actual AgentHQ platform to see how it brings the business of real estate into one connected workspace. Everything here is a fictional demonstration workspace.
          </p>
        </Section>
      </div>

      <Section className="py-10 sm:py-14" data-testid="demo-tour">
        <div className="grid gap-8 lg:grid-cols-[1.55fr_1fr]">
          {/* Stage */}
          <div
            role="group"
            aria-roledescription="carousel"
            aria-label="AgentHQ interactive product tour"
            tabIndex={0}
            onKeyDown={onKeyDown}
            data-testid="demo-tour-stage"
            className="rounded-2xl outline-none ring-brand-gold/40 focus-visible:ring-2"
          >
            <SceneStage scene={scene} active />

            {/* Controls */}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={prev} disabled={index === 0}
                  aria-label="Previous scene" data-testid="demo-tour-prev">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </Button>
                <Button variant="outline" size="sm" onClick={next} disabled={atEnd}
                  aria-label="Next scene" data-testid="demo-tour-next">
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {!reduced && !atEnd && (
                  <Button variant="ghost" size="sm" onClick={() => setPlaying((p) => !p)}
                    aria-label={playing ? "Pause tour" : "Play tour"} aria-pressed={playing}
                    data-testid="demo-tour-playpause" className="text-brand-navy">
                    {playing ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Play tour</>}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={restart} aria-label="Restart tour"
                  data-testid="demo-tour-restart" className="text-brand-navy">
                  <RotateCcw className="h-4 w-4" /> Restart
                </Button>
              </div>
            </div>

            {/* Progress */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs font-semibold text-charcoal/60">
                <span data-testid="demo-tour-progress">{index + 1} of {scenes.length}</span>
                <span>{scene.title}</span>
              </div>
              <div className="mt-2 flex items-center gap-1.5" role="tablist" aria-label="Tour scenes">
                {scenes.map((s, i) => (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={i === index}
                    aria-label={`Go to scene ${i + 1}: ${s.title}`}
                    onClick={() => { manualStop(); goto(i); }}
                    data-testid={`demo-tour-dot-${i}`}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${i === index ? "bg-brand-gold" : i < index ? "bg-brand-gold/40" : "bg-black/10 hover:bg-black/20"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Narration + persona + video slot */}
          <aside className="lg:pt-2" aria-live="polite">
            <Eyebrow>{scene.eyebrow}</Eyebrow>
            <h2 className="mt-2 font-brand text-2xl font-bold text-brand-navy">{scene.title}</h2>
            <p className="mt-3 text-base text-charcoal/75">{scene.narration}</p>

            <div className="mt-6 rounded-xl border border-black/10 bg-brand-light-gray p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-charcoal/40">Demonstration workspace</p>
              <p className="mt-1.5 text-sm font-semibold text-brand-navy">{TOUR_PERSONA.name} · {TOUR_PERSONA.brokerage}</p>
              <p className="text-xs text-charcoal/55">{TOUR_PERSONA.location}</p>
              <p className="mt-2 text-xs text-charcoal/45">{TOUR_PERSONA.note}</p>
            </div>

            {hasVideo && (
              <div className="mt-5" data-testid="demo-tour-video">
                <p className="text-xs font-bold uppercase tracking-widest text-charcoal/40">Prefer to watch?</p>
                <video controls poster={TOUR_VIDEO.poster || undefined} className="mt-2 w-full rounded-xl border border-black/10">
                  <source src={TOUR_VIDEO.url} />
                </video>
              </div>
            )}
          </aside>
        </div>

        {/* Completion CTA (surfaces once the visitor reaches the final scene) */}
        {atEnd && (
          <div className="mt-12 rounded-2xl bg-brand-navy px-6 py-10 text-center text-white" data-testid="demo-tour-complete">
            <h2 className="mx-auto max-w-xl font-brand text-2xl font-bold sm:text-3xl">Ready to run your real estate business differently?</h2>
            <p className="mx-auto mt-3 max-w-lg text-white/70">Start your 14-day free trial. A payment method is required; billing begins after your trial unless you cancel.</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <TrialButton />
              {foundingOpen && (
                <TrialButton founding className="bg-brand-gold text-brand-navy hover:bg-brand-soft-gold">
                  Become a Founding Agent — $19/month
                </TrialButton>
              )}
              <Button variant="ghost" onClick={restart} className="text-white hover:bg-white/10" data-testid="demo-tour-replay">
                <RotateCcw className="h-4 w-4" /> Replay tour
              </Button>
            </div>
            {foundingOpen && (
              <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-soft-gold" data-testid="demo-tour-founding-note">
                <Sparkles className="h-3.5 w-3.5" /> {founding.remaining} of {founding.cap} Founding Agent spots remaining · $19/mo locked
              </p>
            )}
          </div>
        )}
      </Section>
    </MarketingLayout>
  );
}

/* Compact homepage/platform entry that replaces the old placeholder cards. */
export function SeeInActionTour() {
  return (
    <Section className="py-16" data-testid="see-in-action">
      <Eyebrow>See AgentHQ in action</Eyebrow>
      <h2 className="mt-3 font-brand text-3xl font-bold text-brand-navy">See how AgentHQ brings it all together.</h2>
      <p className="mt-3 max-w-2xl text-charcoal/70">Take a guided, interactive walkthrough of the actual AgentHQ platform — from the Daily Business Brief to pipeline, transactions, marketing, compliance, goals, and RESA.</p>
      <div className="mt-8 overflow-hidden rounded-2xl border border-black/10 bg-brand-navy">
        <div className="grid items-center gap-6 p-6 sm:grid-cols-2 sm:p-8">
          <div className="relative overflow-hidden rounded-xl border border-white/10">
            <div className="aspect-[16/10] w-full">
              <img src="/demo-tour/dashboard.webp" alt="Preview of the AgentHQ Dashboard from the demo workspace." loading="lazy"
                className="h-full w-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <span className="pointer-events-none absolute inset-0 grid place-items-center">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-brand-gold/90 text-brand-navy shadow-lg">
                <PlayCircle className="h-7 w-7" />
              </span>
            </span>
          </div>
          <div className="text-white">
            <p className="font-brand text-xl font-bold">An interactive product tour.</p>
            <p className="mt-2 text-sm text-white/70">Click through real AgentHQ screens in about two minutes and see how the whole business connects.</p>
            <div className="mt-5">
              <Link to="/demo">
                <Button className="bg-brand-gold text-brand-navy hover:bg-brand-soft-gold" data-testid="cta-interactive-tour">
                  Take the Interactive Tour <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
